if (!customElements.get('collection-variant-selector')) {
  
  const DEBUG = false; // Auf true setzen für Debugging
  const log = (...args) => DEBUG && console.log('[CVS]', ...args);

  // Konfiguration
  const CVS_CONFIG = {
    CACHE_TTL: 30 * 60 * 1000, // 30 Minuten
    CACHE_PREFIX: 'cvs:v3:',
    MAX_CONCURRENT: 2,
    BATCH_DELAY: 100, // ms
    MAX_PARENTS_PER_REQUEST: 15
  };

  window.__variantSelectorBatch = window.__variantSelectorBatch || {
    pending: new Map(),
    inMemoryCache: new Map(),
    activeRequests: 0,
    requestQueue: [],
    timeout: null,

    // sessionStorage Cache
    getCacheKey(parentArticle) {
      const lang = window.ISO_CODE || 'DE';
      return `${CVS_CONFIG.CACHE_PREFIX}${lang}:${parentArticle}`;
    },

    readCache(parentArticle) {
      // Erst In-Memory prüfen (schneller)
      if (this.inMemoryCache.has(parentArticle)) {
        return this.inMemoryCache.get(parentArticle);
      }
      // Dann sessionStorage
      try {
        const key = this.getCacheKey(parentArticle);
        const cached = sessionStorage.getItem(key);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp > CVS_CONFIG.CACHE_TTL) {
          sessionStorage.removeItem(key);
          return null;
        }
        // In Memory speichern für schnelleren Zugriff
        this.inMemoryCache.set(parentArticle, data);
        return data;
      } catch (e) {
        return null;
      }
    },

    writeCache(parentArticle, data) {
      this.inMemoryCache.set(parentArticle, data);
      try {
        const key = this.getCacheKey(parentArticle);
        sessionStorage.setItem(key, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Storage voll - alte Einträge löschen
        this.clearOldCache();
      }
    },

    clearOldCache() {
      try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith(CVS_CONFIG.CACHE_PREFIX)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.slice(0, Math.ceil(keysToRemove.length / 2)).forEach(k => {
          sessionStorage.removeItem(k);
        });
      } catch (e) {}
    },

    proxyImageUrl(url) {
      if (!url) return '';
      // Shop-Domain statt cdn.shopify.com
      return url.replace(
        /https:\/\/cdn\.shopify\.com\/s\/files\/1\/(\d+\/)+/,
        window.location.origin + '/cdn/shop/'
      );
    },
    
    async fetchBatch(parentArticles) {
      const query = parentArticles.map(p => `tag:"parent:${p}"`).join(' OR ');
      log('Batch Query für', parentArticles.length, 'parents');
      
      try {
        const response = await fetch("/api/2024-10/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": "573612d745d5f109fa297da9764819e0",
          },
          body: JSON.stringify({
            variables: { first: 250, query },
            query: `
              query getProducts($first: Int, $query: String) @inContext(language: ${window.ISO_CODE || 'DE'}) {
                products(first: $first, query: $query) {
                  edges {
                    node {
                      id
                      title
                      handle
                      totalInventory
                      featuredImage { url }
                      tags
                      parent: metafield(namespace: "akeneo", key: "artikelnummer_parent") { value }
                      search_color: metafield(namespace: "custom", key: "suchfarbe") { value }
                      color: metafield(namespace: "custom", key: "color") { value }
                    }
                  }
                }
              }
            `,
          })
        });
        
        const data = await response.json();
        if (data.errors) {
          console.error('[CVS] GraphQL Errors:', data.errors);
          return;
        }
        
        const edges = data?.data?.products?.edges || [];
        log('Products found:', edges.length);
        
        // Produkte nach Parent gruppieren
        const productsByParent = new Map();
        parentArticles.forEach(p => productsByParent.set(p, []));
        
        edges.forEach(({ node }) => {
          const parentTag = node.tags?.find(t => t.startsWith('parent:'));
          const parent = parentTag ? parentTag.replace('parent:', '') : node.parent?.value;
          
          if (!parent || !productsByParent.has(parent)) return;
          
          if (node.totalInventory > 0) {
            productsByParent.get(parent).push({
              handle: node.handle,
              title: node.title,
              featuredImage: node.featuredImage ? { 
                url: this.proxyImageUrl(node.featuredImage.url) 
              } : null,
              search_color: node.search_color?.value,
              color: node.color?.value,
            });
          }
        });
        
        // Cache schreiben
        productsByParent.forEach((products, parent) => {
          this.writeCache(parent, products);
        });
        
        return productsByParent;
        
      } catch (err) {
        console.error('[CVS] Fetch error:', err);
        return null;
      }
    },

    processQueue() {
      while (this.activeRequests < CVS_CONFIG.MAX_CONCURRENT && this.requestQueue.length > 0) {
        const { parents, callbacks } = this.requestQueue.shift();
        this.activeRequests++;
        
        this.fetchBatch(parents)
          .then(productsByParent => {
            if (productsByParent) {
              callbacks.forEach((cbs, parent) => {
                const products = productsByParent.get(parent) || this.readCache(parent) || [];
                log('Executing', cbs.length, 'callbacks for', parent);
                cbs.forEach(cb => cb(products));
              });
            }
          })
          .catch(err => {
            console.error('[CVS] Queue error:', err);
          })
          .finally(() => {
            this.activeRequests--;
            this.processQueue();
          });
      }
    },
    
    request(parentArticle, callback) {
      // Cache prüfen
      const cached = this.readCache(parentArticle);
      if (cached) {
        log('Cache hit for', parentArticle);
        callback(cached);
        return;
      }
      
      // Pending-Callbacks sammeln
      if (!this.pending.has(parentArticle)) {
        this.pending.set(parentArticle, []);
      }
      this.pending.get(parentArticle).push(callback);
      
      // Debounce
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        const allCallbacks = new Map(this.pending);
        this.pending.clear();
        
        const parents = Array.from(allCallbacks.keys());
        log('Scheduling batch for', parents.length, 'parents');
        
        // In Batches aufteilen
        for (let i = 0; i < parents.length; i += CVS_CONFIG.MAX_PARENTS_PER_REQUEST) {
          const batchParents = parents.slice(i, i + CVS_CONFIG.MAX_PARENTS_PER_REQUEST);
          const batchCallbacks = new Map();
          batchParents.forEach(p => batchCallbacks.set(p, allCallbacks.get(p)));
          
          this.requestQueue.push({
            parents: batchParents,
            callbacks: batchCallbacks
          });
        }
        
        this.processQueue();
      }, CVS_CONFIG.BATCH_DELAY);
    }
  };

  class CollectionVariantSelector extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
    }

    connectedCallback() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.observer.disconnect();
            this.loadVariants();
          }
        });
      }, { rootMargin: '200px' });
      
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    loadVariants() {
      const parentProduct = this.getAttribute("data-parent");
      if (!parentProduct) return;

      window.__variantSelectorBatch.request(parentProduct, (products) => {
        this.render(products);
      });
    }

    render(products) {
      if (!products.length) return;

      let colorsInnerHTML = "";
      let imagesInnerHTML = "";

      products.slice(0, 4).forEach((p) => {
        if (window.themeSwatch?.[p.search_color]) {
          colorsInnerHTML += `
            <span class="product-sibling-color" 
                  style="background-color: ${window.themeSwatch[p.search_color]}" 
                  title="${p.color || ''}">
            </span>
          `;
        }

        if (p.featuredImage?.url) {
          // WebP Format erzwingen
          const baseUrl = p.featuredImage.url.split('?')[0];
          const smallImageUrl = baseUrl + '?width=100&height=100&format=webp';
          imagesInnerHTML += `
            <a href="/products/${p.handle}" title="${p.title}" 
               data-color="${p.color || ''}" 
               class="product-sibling sibling-link">
              <img src="${smallImageUrl}" width="50" height="50" alt="${p.title}" class="sibling-image" loading="lazy">
            </a>
          `;
        }
      });

      if (products.length > 4) {
        colorsInnerHTML += `<span class="product-sibling-color-plus">+${products.length - 4}</span>`;
        imagesInnerHTML += `<span class="product-sibling-plus">+${products.length - 4}</span>`;
      }

      const colorsEl = this.querySelector(".product-sibling-colors");
      const imagesEl = this.querySelector(".product-sibling-images");
      
      if (colorsEl) colorsEl.innerHTML = colorsInnerHTML;
      if (imagesEl) imagesEl.innerHTML = imagesInnerHTML;
    }
  }

  customElements.define('collection-variant-selector', CollectionVariantSelector);
}
